import { autoinject, transient, useView } from "aurelia-framework";
import { MepGetYearOverYearEmissionStatisticsRequest } from "../../../../../../models/mep/mep-year-on-year.models";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { MepYearOverYearService } from "../../../../../../services/mep/mep-year-on-year-service";
import * as Enumerable from "linq";
import { MepLookupItem, MepPortDetails } from "../../../../../../models/mep/mep.models";
import { MepLookupService } from "../../../../../../services/mep/mep-lookup.service";
import { Lookups } from "../../../../../../constants/mep/mep-lookup.constants";
import { MepYoYChartColors } from "../../../../../../constants/mep/mep-year-on-year.constants";
import { Claims } from "../../../../../../constants/authentication/claims";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { MepYearOverYearComparisonYearFilter } from "./partials/mep-year-over-year-comparison-year-filter/mep-year-over-year-comparison-year-filter";
import { MepYearOverYearStatisticItem } from "./partials/mep-year-over-year-statistic-item/mep-year-over-year-statistic-item";
import { MepYearOverYearComparisonLineChart } from "./partials/mep-year-over-year-comparison-line-chart/mep-year-over-year-comparison-line-chart";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

@autoinject
@transient()
@useView('views/pages/modules/mep/tabs/mep-year-over-year-comparison/mep-year-over-year-comparison.html')
export class MepYearOverYearComparison {

    //this is so that the VM is always created new 
    determineActivationStrategy() {
        return "replace";
    }

    lineChartViewModel: MepYearOverYearComparisonLineChart;
    public minFilters: number = 2;
    portId: number;
    public portDetails: MepPortDetails;
    loadingYears: boolean = false;
    loadingStats: boolean = false;
    years: MepLookupItem[] = []; // this is used to set the base years for relative 
    yearsToCompareVms: MepYearOverYearComparisonYearFilter[] = [];//stores the view models for the years to compare
    mainStatisticsViewModels: MepYearOverYearStatisticItem[];
    onSelectedYearChangedFunc: () => void;
    getRequestFunc: () => MepGetYearOverYearEmissionStatisticsRequest;
    getYearColorFunc: (year: number) => string;

    //Claims
    canGeneratePortDashboardReport: boolean;

    constructor ( private mepLookupService: MepLookupService, private mepYearOnYearService: MepYearOverYearService, 
        private notificationClientService: NotificationClientService, userClaimsService: UserClaimsService,
        private dialogClientService: DialogClientService, private configService: ConfigService) {
        this.onSelectedYearChangedFunc = () => this.onSelectedYearChanged();
        this.getRequestFunc = () => this.getRequest();
        this.getYearColorFunc = (year) => this.getYearColor(year);
        this.canGeneratePortDashboardReport = userClaimsService.hasPermission(Claims.CanGeneratePortDashboardReport);
    }

    setYearColor(item: MepYearOverYearComparisonYearFilter) {
        const itemIndex = this.yearsToCompareVms.findIndex(x => x == item);
        const colorIndex = itemIndex % MepYoYChartColors.length;
        const color = MepYoYChartColors[colorIndex] ? MepYoYChartColors[colorIndex] : MepYoYChartColors[0];
        this.yearsToCompareVms[itemIndex].color = color;
    }

    getYearColor(year: number): string {
        if (!this.yearsToCompareVms || this.yearsToCompareVms.length == 0)
            return MepYoYChartColors[0];

        const yearToGet = Enumerable.from(this.yearsToCompareVms).where(x => x.selectedYear && x.selectedYear.id == year).firstOrDefault();
        return yearToGet ? yearToGet.color : MepYoYChartColors[0];
    }

    activate(param) {
        this.portId = param.portId;
        this.portDetails = <MepPortDetails>{
            portId: param.portId,
            portName: param.portName,
            showPortName: param.showPortName,
            supportsCargoType: param.supportsCargoType,
            portImageUrl: param.portImageUrl,
        };
        this.lineChartViewModel = new MepYearOverYearComparisonLineChart(this.mepYearOnYearService, this.notificationClientService, this.getRequestFunc, this.getYearColorFunc, this.configService);
        this.resetYearsToCompare();
    }

    private resetYearsToCompare() {
        this.yearsToCompareVms = [];
        const baseYearVm = new MepYearOverYearComparisonYearFilter(this.onSelectedYearChangedFunc);
        baseYearVm.isBaseYear = true;
        this.yearsToCompareVms.push(baseYearVm);
        this.setYearColor(baseYearVm);
    }

    addYearToCompare() {
        const yearVm = new MepYearOverYearComparisonYearFilter(this.onSelectedYearChangedFunc);
        yearVm.isBaseYear = false;
        this.yearsToCompareVms.push(yearVm);
        this.setYearColor(yearVm);
        this.applyFilteredYearsForFilterVMs();//set the years for the VM while ensure integrity to others
    }

    removeYearToCompare(item) {
        const index = this.yearsToCompareVms.indexOf(item);
        if (index >= 0) {
            this.yearsToCompareVms.splice(index, 1);
            this.applyFilteredYearsForFilterVMs();//set the years for the VM while ensure integrity to others
            this.onSelectedYearChanged();
        }
    }

    async attached() {
        
        this.initialize();        
    }

    initialize()
    {

        this.lineChartViewModel.init(this.portId); // init this viewmodel which will load its own set of data 

        //here do the api calls to load the data 
        this.loadingYears = true;
        this.loadingStats = true;

        this.lineChartViewModel.loadingInitData = true;
        this.mepLookupService.getLookups({
            portId: this.portId,
            queries: [Lookups.FinancialYears,
            Lookups.EmissionTypes,
            Lookups.OperationModes,
            Lookups.VesselTypeGroups,
            Lookups.PointOfInterests
            ],
            emissionTypesIncludeAllYears: false
        })
            .then(response => {

                this.loadingYears = false;
                this.lineChartViewModel.loadingInitData = false;

                if (response.hasError) {
                    this.notificationClientService.show("MEP year on year", "Error retrieving the years!", NotificationType.Error);
                } else {

                    this.years = response.allValuesByQuery[Lookups.FinancialYears];
                    this.lineChartViewModel.emissionTypes = response.allValuesByQuery[Lookups.EmissionTypes];
                    this.lineChartViewModel.vesselTypeGroups = response.allValuesByQuery[Lookups.VesselTypeGroups];
                    this.lineChartViewModel.pointsOfInterests = response.allValuesByQuery[Lookups.PointOfInterests];
                    this.lineChartViewModel.operationModes = response.allValuesByQuery[Lookups.OperationModes];

                    //notify that the years changed 
                    this.applyFilteredYearsForFilterVMs();//set the years for the VM while ensure integrity to others
                }
                this.loadingStats = false;
            }).catch(() => {
                this.notificationClientService.show("MEP year on year", "Error retrieving the years!", NotificationType.Error);
                this.lineChartViewModel.loadingInitData = false;
                this.loadingYears = false;
            });

        //set up the viewmodels which drive the bar charts (statistics)
        this.mainStatisticsViewModels = [
            new MepYearOverYearStatisticItem("Vessel Calls", "anchor", x => x.vesselCalls, x => x.percentageChangeVesselCallsFromBaseYear, this.getYearColorFunc),
            new MepYearOverYearStatisticItem("Distinct Vessel Count", "ship-tinner", x => x.vesselCount, x => x.percentageChangeVesselCountFromBaseYear, this.getYearColorFunc),
            new MepYearOverYearStatisticItem("Tugboat Jobs", "tugboat", x => x.tugboatJobs, x => x.percentageChangeTugboatJobsFromBaseYear, this.getYearColorFunc)
        ];
    }

    onSelectedYearChanged() {
        this.loadingStats = true;
        const baseYear = Enumerable.from(this.yearsToCompareVms).where(x => x.selectedYear && x.isBaseYear).firstOrDefault();
        if (baseYear && baseYear.selectedYear) {
            this.mainStatisticsViewModels.forEach(x => x.setBaseYear(baseYear.selectedYear.id));
            this.lineChartViewModel.setBaseYear(baseYear.selectedYear.id);
        }

        this.refreshLookups(this.yearsToCompareVms.map(x=> x?.selectedYear?.id));
    }
    

    refreshLookups(years?: number[], loadData: boolean = true) {
        return new Promise<string>((resolve, reject) => {
            this.mepLookupService.getLookups({
                portId: this.portId,
                years: years,
                queries: [
                Lookups.EmissionTypes,
                Lookups.OperationModes,
                Lookups.PointOfInterests,
                Lookups.VesselTypeGroups
                ],
                emissionTypesIncludeAllYears: false
            }).then(response => {
                if (response.hasError) {
                    reject();
                } else {
                    this.lineChartViewModel.emissionTypes = response.allValuesByQuery[Lookups.EmissionTypes];
                    this.lineChartViewModel.pointsOfInterests = response.allValuesByQuery[Lookups.PointOfInterests];
                    this.lineChartViewModel.operationModes = response.allValuesByQuery[Lookups.OperationModes];
                    this.lineChartViewModel.vesselTypeGroups = response.allValuesByQuery[Lookups.VesselTypeGroups];
                }

                if (this.portId && loadData) {
                    this.loadYearOnYearStats(); // load the data
                    this.lineChartViewModel.applyAllFilters(); // filter the data for the line chart
                    this.applyFilteredYearsForFilterVMs();
                }
                resolve(null);
            }).catch(() => {
                reject();
            });
        });
    }

    emissionTypesConverter(values: MepLookupItem[]) {
        if (values) {
            values.forEach(v => {
                if (v && v.displayName && v.displayName)
                    v.name = v.displayName;
            });
        }
        return values;
    }

    applyFilteredYearsForFilterVMs() {
        //filter the years available in filters 
        const allSelectedYearsByVms = Enumerable.from(this.yearsToCompareVms).where(x => x.selectedYear != null).select(x => x.selectedYear);
        this.yearsToCompareVms.forEach(yearsToCompareVm => {
            //if the vm has a selected year we cannot filter it 
            const yearsToFilterForVm = yearsToCompareVm.selectedYear ?
                allSelectedYearsByVms.where(x => x.id != yearsToCompareVm.selectedYear.id)
                : allSelectedYearsByVms;
            yearsToCompareVm.years = Enumerable.from(this.years).where(x => !yearsToFilterForVm.any(y => y.id == x.id)).toArray();
        });
    }

    getRequest(): MepGetYearOverYearEmissionStatisticsRequest {
        const yearsToCompare = Enumerable.from(this.yearsToCompareVms).where(x => x.selectedYear != null);

        const selectedBaseYear = yearsToCompare.firstOrDefault(x => x.isBaseYear);
        if (!selectedBaseYear || !selectedBaseYear.selectedYear || !selectedBaseYear.selectedYear.id) {
            return null;
        }

        const comparisonYears = this.yearsToCompareVms ?
            yearsToCompare.where(x => !x.isBaseYear).select(x => x.selectedYear.id).distinct().toArray()
            : [];


        const request: MepGetYearOverYearEmissionStatisticsRequest = {
            portId: this.portId,
            baseYear: selectedBaseYear.selectedYear.id,
            comparisonYears: comparisonYears
        };
        return request;
    }

    loadYearOnYearStats() {
        const request: MepGetYearOverYearEmissionStatisticsRequest = this.getRequest();
        if(request == null || request.baseYear == 0 || request.portId== 0){
            this.loadingStats = false;
            return;
        }
        this.loadingStats = true;
        this.mepYearOnYearService.getYearOverYearEmissionStatistics(request)
            .then(response => {
                if (response.hasError) {
                    this.notificationClientService.show("MEP year on year", "Error retrieving the year on year stats!", NotificationType.Error);
                } else {

                    //we want to keep the order of how the years were requested.
                    const orderedStatistics = [];
                    const originalRequest = this.getRequest();                   
                    orderedStatistics.push(response.statistics.filter(x => x.year == originalRequest.baseYear)[0]);
                    originalRequest.comparisonYears.forEach(x => {
                        orderedStatistics.push(response.statistics.filter(y => y.year == x)[0]);
                    });

                    //call the view models to update their data 
                    this.mainStatisticsViewModels.forEach(x => x.refreshData(orderedStatistics));
                }
                this.loadingStats = false;
            }).catch(() => {
                this.notificationClientService.show("MEP year on year", "Error retrieving the year on year stats!", NotificationType.Error);
                this.loadingStats = false;
            });
    }

    generateReport() {
        if(!this.loadingYears && !this.loadingStats && !this.lineChartViewModel.isRefreshingData && !this.lineChartViewModel.loadingInitData) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.PrintMepYearOverYearDialog, this, true, false, true);
        }
        else {
            this.notificationClientService.show("Page is loading, please try again once its loaded", "", NotificationType.Info);
        }
    }

    refresh(data?: {portId: number, portName: string, showPortName: boolean, supportsCargoType: boolean, portImageUrl: string})
    {
        this.portId = data.portId;
        this.portDetails = <MepPortDetails>{
            portId: data.portId,
            portName: data.portName,
            showPortName: data.showPortName,
            supportsCargoType: data.supportsCargoType,
            portImageUrl: data.portImageUrl,
        };
        this.resetYearsToCompare();
        this.lineChartViewModel = new MepYearOverYearComparisonLineChart(this.mepYearOnYearService, this.notificationClientService, this.getRequestFunc, this.getYearColorFunc, this.configService);
        this.initialize();
    }
}